import { useEffect, useMemo, useState } from 'react';

import { H_TAG_NAMES } from '@/constant/common';
import { useTOCDataCtx } from '@/hooks/ctx';
import { equals, flow, head, isNotEmpty, toLowerCase } from '@/utils/common';

import { useObserver } from '../useObserver';

const getHChildElements = (element: Element) =>
  Array.from(element.children).filter(child => H_TAG_NAMES.includes(toLowerCase(child.tagName)));

const applyObserverToElementPred = (element: Element) => {
  const isSection = (el: Element) => equals(toLowerCase(el.tagName), 'section');
  if (Array.from(element.children).some(isSection)) return false;

  return flow(element, [getHChildElements, isNotEmpty]);
};

const transformFn = (element: Element) => head(getHChildElements(element)) as Element;

export const useAutoUpdateActiveTOC = (shouldSkip?: boolean) => {
  const [elements, setElements] = useState<NodeListOf<Element>>([] as unknown as NodeListOf<Element>);
  const { handleChangeActiveTOCElement, activeTOCElement } = useTOCDataCtx();

  useEffect(() => {
    const elements = document.querySelectorAll('section[id]');
    setElements(elements);
    if (activeTOCElement.includes('#')) return;
    const headValidElement = [...elements].find(applyObserverToElementPred);
    if (headValidElement) {
      handleChangeActiveTOCElement(headValidElement.id || '');
    }
  }, []);

  const isAboutToEndOfList = [...elements].slice(-3).some(el => {
    return equals(el.id, activeTOCElement);
  });

  const observerConfig = useMemo(() => {
    return {
      threshold: 0,
      rootMargin: isAboutToEndOfList ? undefined : '-10% 0px -80% 0px',
    };
  }, [isAboutToEndOfList]);

  useObserver({
    shouldSkip: shouldSkip,
    observableElements: elements,
    onObserve: entry => {
      const parentElementId = entry.target.parentElement?.id;

      if (parentElementId && entry.isIntersecting) {
        handleChangeActiveTOCElement(parentElementId, () => !Boolean(window.location.hash));
      }
    },
    beforeApplyObserver: {
      predicate: applyObserverToElementPred,
      transform: transformFn,
    },
    observerConfig,
  });
};
